<xi-drawer
  #drawer
  [open]="open()"
  [interceptClose]="true"
  (onClose)="cancel()"
  color="CX"
  [silverBg]="true"
>
  <ng-container *transloco="let t; read: 'configurations.create-drawer'">
    <div slot="title">{{ t('title') }}</div>

    <div slot="body">
      @if (isSaving) {
        <div class="create-configuration__loading">
          <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
        </div>
      }

      <form [formGroup]="configForm" class="create-configuration__form">
        <section class="create-configuration__form-section">
          <div>
            <h3>{{ t('details-header') }}</h3>

            <p class="create-configuration__form-section-description">
              {{ t('details-content') }}
            </p>
          </div>

          <div class="create-configuration__form-section--field">
            <xi-input-field
              [label]="t('configuration-name')"
              [placeholder]="t('enter-here')"
              formControlName="configName"
              icon="cancel"
              [status]="configNameStatus"
              (onChange)="onValidateConfigName()"
              (onIconClick)="configForm.patchValue({ configName: '' }); onValidateConfigName()"
              required
            ></xi-input-field>
          </div>

          <div
            class="create-configuration__form-section--field create-configuration__form-section-text-area"
          >
            <xi-text-area [label]="t('description')" formControlName="configNotes"></xi-text-area>
          </div>

          <app-tag-editor
            class="create-configuration__form-section--field configuration-details__tags"
            [tags]="configDto().tags"
            formControlName="tagEditor"
            [configuration]="configDto()"
            [editable]="isTagEditorEditable"
            [removable]="true"
            (tagAdded)="onTagAdded($event)"
            (tagRemoved)="onTagRemoved($event)"
          ></app-tag-editor>
        </section>

        @if (!inputFile() && configType() === 'standard') {
          <section class="create-configuration__form-section">
            <div>
              <h3>{{ t('base-configuration-header') }}</h3>
              <p class="create-configuration__form-section-description">
                {{ t('base-configuration-content') }}
              </p>
            </div>
            <div class="create-configuration__form-section--field">
              @if (!configTemplates) {
                <div class="create-configuration__loading">
                  <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
                </div>
              }
              <app-lex-select
                [value]="selectedLanguage"
                [options]="languages"
                [placeholder]="t('select-language')"
                [labelFormatter]="languageLabelFormatter"
                (valueChange)="onLanguageChanged($event)"
                >{{ t('filter-by-language') }}
              </app-lex-select>
              <ng-template
                #tConfigTemplate
                let-header="header"
                let-templateDs="templateDs"
                let-templateType="templateType"
              >
                <div class="create-configuration__expansion-panel-wrapper">
                  <mat-expansion-panel
                    [disabled]="!templateDs.length"
                    [expanded]="selectedTemplateType === templateType"
                    class="mat-elevation-z0"
                  >
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        {{ header }}
                      </mat-panel-title>
                      <mat-panel-description>
                        @if (!templateDs.length) {
                          <span>{{ t('template-not-available') }}</span>
                        }
                      </mat-panel-description>
                      @if (selectedTemplateType === templateType) {
                        <xi-icon
                          icon="circle_check_filled"
                          color="orca"
                          class="create-configuration__template-row-check"
                        ></xi-icon>
                      }
                    </mat-expansion-panel-header>
                    <div>
                      @for (source of templateDs; track source) {
                        <div class="create-configuration__template-row">
                          <xi-radio
                            [checked]="isTemplateSelected(source.language_id, source.id)"
                            (onChange)="selectTemplate(templateType, source.language_id, source.id)"
                            [label]="source.name"
                            [disabled]="source.layer"
                            [matTooltip]="source.layer ? t('standard-from-layered') : null"
                            color="CX"
                          ></xi-radio>
                          <app-configuration-pill
                            [templateType]="templateType"
                          ></app-configuration-pill>
                        </div>
                      }
                    </div>
                  </mat-expansion-panel>
                </div>
              </ng-template>
              <div class="create-configuration__expansion-panel-content-wrapper">
                <mat-radio-group>
                  <ng-container
                    *ngTemplateOutlet="
                      tConfigTemplate;
                      context: {
                        header: t('language-template'),
                        templateType: 'language',
                        templateDs: filteredLanguageTemplates,
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      tConfigTemplate;
                      context: {
                        header: t('configurations'),
                        templateType: 'configuration',
                        templateDs: filteredConfigurations,
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      tConfigTemplate;
                      context: {
                        header: t('industry-pack'),
                        templateType: 'pack',
                        templateDs: filteredIndustryPacks,
                      }
                    "
                  ></ng-container>
                </mat-radio-group>
              </div>
            </div>
          </section>
        }
        @if (!inputFile() && configType() === 'layered') {
          <section class="create-configuration__form-section">
            <div>
              <h3>{{ t('configuration-layers-header') }}</h3>
              <p class="create-configuration__form-section-description">
                {{ t('configuration-layers-content') }}
              </p>
            </div>
            <div class="create-configuration__form-section--field">
              @if (!configTemplates) {
                <div class="create-configuration__loading">
                  <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
                </div>
              }
              <app-lex-select
                [value]="selectedLanguage"
                [options]="languages"
                [placeholder]="t('select-language')"
                [labelFormatter]="languageLabelFormatter"
                (valueChange)="onLanguageChanged($event)"
                >{{ t('filter-by-language') }}
              </app-lex-select>
              <ng-template
                #tConfigsToAdd
                let-header="header"
                let-icon="icon"
                let-templateDs="templateDs"
              >
                <div class="create-configuration__expansion-panel-wrapper">
                  <mat-expansion-panel
                    [disabled]="isDataSourceEmpty(templateDs)"
                    [expanded]="true"
                    class="mat-elevation-z0"
                  >
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <xi-icon
                          [icon]="icon"
                          class="create-configuration__template-header-icon"
                        ></xi-icon>
                        {{ header }}
                      </mat-panel-title>
                      <mat-panel-description>
                        @if (isDataSourceEmpty(templateDs)) {
                          <span>{{ t('template-not-available') }}</span>
                        }
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                    <div>
                      @for (category of templateDs | keyvalue: entryOrder; track category) {
                        @for (source of category.value; track source) {
                          <div class="create-configuration__template-row">
                            <mat-checkbox
                              [checked]="isTemplateSelected(source.language_id, source.id)"
                              (change)="toggleTemplate(source.language_id, source.id)"
                            >
                              {{ source.name }}
                            </mat-checkbox>
                            @if (icon === 'single_layer') {
                              <app-configuration-pill [templateType]="category.key">
                              </app-configuration-pill>
                            }
                          </div>
                        }
                      }
                    </div>
                  </mat-expansion-panel>
                </div>
              </ng-template>
              <div>
                <h5>{{ t('add-configurations-header') }}</h5>
                <div class="create-configuration__expansion-panel-content-wrapper">
                  <ng-container
                    *ngTemplateOutlet="
                      tConfigsToAdd;
                      context: {
                        header: t('standard-configurations'),
                        icon: 'single_layer',
                        templateDs: filteredStandardConfigs,
                      }
                    "
                  ></ng-container>
                  <ng-container
                    *ngTemplateOutlet="
                      tConfigsToAdd;
                      context: {
                        header: t('layered-configurations'),
                        icon: 'multiple_layer',
                        templateDs: filteredLayeredConfigs,
                      }
                    "
                  ></ng-container>
                </div>
              </div>
            </div>
          </section>
        }
        @if (!inputFile()) {
          <section class="create-configuration__form-section">
            <div>
              <h3>{{ t('content-header') }}</h3>
              <p class="create-configuration__form-section-description">
                {{ t('content-content') }}
              </p>
            </div>
            <div class="create-configuration__form-section--field">
              @for (cv of contentValues; track cv) {
                <div class="create-configuration__radio-group">
                  <xi-radio
                    [value]="cv"
                    [label]="cv.label"
                    formControlName="contentType"
                    name="contentTypeRadio"
                    [checked]="cv === contentValues[0]"
                    color="CX"
                  ></xi-radio>
                  <xi-icon
                    icon="info"
                    color="orca"
                    iconSize="large"
                    [tooltipProps]="{ text: cv.tooltip, direction: 3 }"
                  ></xi-icon>
                </div>
              }
            </div>
          </section>
          <section class="create-configuration__form-section">
            <div>
              <h3>{{ t('response-type-header') }}</h3>
              <p class="create-configuration__form-section-description">
                {{ t('response-type-content') }}
              </p>
            </div>
            <div class="create-configuration__form-section--field">
              @for (rt of responseTypeValues; track rt) {
                <div class="create-configuration__radio-group">
                  <xi-radio
                    [value]="rt.value"
                    [label]="rt.label"
                    formControlName="responseType"
                    name="responseTypeRadio"
                    [checked]="rt.value === 'POLLING'"
                    color="CX"
                  >
                  </xi-radio>
                  <xi-icon
                    icon="info"
                    color="orca"
                    iconSize="large"
                    [tooltipProps]="{ text: rt.tooltip, direction: 3 }"
                  ></xi-icon>
                </div>
              }
              <mat-form-field class="full-width">
                <input
                  matInput
                  placeholder="https://your-server-address/callback.json"
                  formControlName="callbackUrl"
                />
                <mat-hint>{{ t('callback-url-hint') }}</mat-hint>
                @if (configForm.controls.callbackUrl.hasError('urlInvalid')) {
                  <mat-error>
                    {{ t('error-callback-url-invalid') }}
                  </mat-error>
                }
                @if (configForm.controls.callbackUrl.hasError('urlNotCallback')) {
                  <mat-error>
                    {{ t('error-callback-url-not-callback') }}
                  </mat-error>
                }
              </mat-form-field>
            </div>
          </section>
        } @else {
          <h2>{{ t('import-from-file-header') }}</h2>
          <p>{{ t('import-from-file-content') }}</p>
          <hr />
          <section
            class="create-configuration__form-section create-configuration__form-section--borderless"
          >
            <div>
              <h3>{{ t('import-source') }}</h3>
              <table class="create-configuration__source-file">
                <thead>
                  <th>{{ t('import-file-name') }}</th>
                  <th>{{ t('import-file-type') }}</th>
                  <th>{{ t('import-file-size') }}</th>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ fileName }}</td>
                    <td>{{ fileType }}</td>
                    <td>{{ inputFile().size | humanizeInt }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="create-configuration__change-source">
                <span (click)="cancel(true)">{{ t('import-another') }}</span>
              </div>
            </div>
            <div class="create-configuration__form-section--field">
              <app-configuration-content-form
                [configurationDto]="configDto()"
                (formChange)="onConfigIncludeChanged($event)"
                (accountFeatureCheck)="onAccountFeatureChecked($event)"
              ></app-configuration-content-form>
            </div>
          </section>
        }
      </form>

      @if (missingAccountFeatures.length) {
        <div class="create-configuration__errors">
          {{ t('we-have-detected') }}
          <span [matTooltip]="missingAccountFeatures.join('\n')">
            {{
              t('missing-configuration-settings', { count: missingAccountFeatures.length })
            }}</span
          >
          {{ t('import-feature-excluding-missing') }}
        </div>
      }
    </div>

    <div slot="footer" class="create-configuration__actions">
      <xi-default-button
        [text]="t('cancel')"
        fill="text"
        [disabled]="isSaving"
        (onClick)="cancel()"
      ></xi-default-button>

      <div class="xi-loader-button-container">
        <div [matTooltip]="configIncludeData?.tooltip ?? ''">
          <xi-default-button
            [text]="t('confirm')"
            [disabled]="
              !configForm.valid || (configIncludeData && !configIncludeData.valid) || isSaving
            "
            (onClick)="createOrImport()"
          >
          </xi-default-button>
        </div>

        @if (isSaving) {
          <div class="xi-loader-container__inline-overlay">
            <xi-animation
              [height]="75"
              [width]="75"
              [loop]="true"
              animation="cx_loader"
            ></xi-animation>
          </div>
        }
      </div>
    </div>
  </ng-container>
</xi-drawer>
